import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { parseError } from '@/lib/errors'
import {
  type Sequence,
  SequenceStatus,
  usePauseSequence,
  useResumeSequence,
  useStopSequence,
} from '@/models/sequence'
import { leadSequencesPath } from './sequenceStatus'

const onErr = (e: unknown) => toast.error(parseError(e))

/** Same lifecycle actions as the lead's Sequences tab; a draft is edited and started there. */
export function SequenceControls({ sequence }: { sequence: Sequence }) {
  const pause = usePauseSequence()
  const resume = useResumeSequence()
  const stop = useStopSequence()

  async function handleStop() {
    try {
      await stop.mutateAsync(sequence.id)
    } catch (err) {
      onErr(err)
    }
  }

  if (sequence.status === SequenceStatus.RUNNING) {
    return (
      <Button
        variant="outline"
        loading={pause.isPending ? 'Pausing…' : undefined}
        onClick={() => pause.mutate(sequence.id, { onError: onErr })}
      >
        Pause
      </Button>
    )
  }
  if (sequence.status === SequenceStatus.PAUSED) {
    return (
      <div className="flex gap-2">
        <Button
          loading={resume.isPending ? 'Resuming…' : undefined}
          onClick={() => resume.mutate(sequence.id, { onError: onErr })}
        >
          Resume
        </Button>
        <ConfirmationDialog
          title="Stop this sequence?"
          description="Its remaining steps won't run, and a stopped sequence can't be restarted."
          confirmLabel="Stop sequence"
          onConfirm={handleStop}
        >
          <Button variant="destructive">Stop</Button>
        </ConfirmationDialog>
      </div>
    )
  }
  if (sequence.status === SequenceStatus.DRAFT && sequence.salesLead) {
    return (
      <Button variant="outline" href={leadSequencesPath(sequence.salesLead)}>
        Edit on lead
      </Button>
    )
  }
  return null
}
